import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { BlogClient } from './client';
import type { Article, ArticleIndex } from './types';

const ARTICLES_PER_PAGE = 10;

interface BlogContextType {
  client: BlogClient;
  articles: ArticleIndex[];
  loading: boolean;
  error: Error | null;
  currentPage: number;
  totalPages: number;
  setPage: (page: number) => void;
  refreshArticles: (page?: number) => Promise<void>;
  getArticle: (slug: string) => Promise<Article | null>;
}

interface BlogProviderProps {
  apiKey: string;
  children: React.ReactNode;
}

const BlogContext = createContext<BlogContextType | null>(null);

export function BlogProvider({ apiKey, children }: BlogProviderProps) {
  const [client] = useState(() => new BlogClient(apiKey));
  const [articles, setArticles] = useState<ArticleIndex[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const mountedRef = useRef(false);
  const fetchingRef = useRef(false);

  const refreshArticles = useCallback(async (pageNum: number = 1) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    try {
      setLoading(true);
      setError(null);
      const result = await client.getArticles(pageNum);
      if (mountedRef.current) {
        setArticles(result.articles);
        setTotalArticles(result.total);
        setPage(pageNum);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error('Failed to fetch articles'));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
      fetchingRef.current = false;
    }
  }, [client]);

  useEffect(() => {
    mountedRef.current = true;
    refreshArticles();
    return () => {
      mountedRef.current = false;
    };
  }, [refreshArticles]);

  const getArticle = useCallback(async (slug: string): Promise<Article | null> => {
    try {
      return await client.getArticle(slug);
    } catch (err) {
      console.error('Failed to fetch article:', err);
      return null;
    }
  }, [client]);

  const contextValue = {
    client,
    articles,
    loading,
    error,
    currentPage: page,
    totalPages: Math.ceil(totalArticles / ARTICLES_PER_PAGE),
    setPage,
    refreshArticles,
    getArticle,
  };

  return (
    <BlogContext.Provider value={contextValue}>
      {children}
    </BlogContext.Provider>
  );
}

export function useBlog() {
  const context = useContext(BlogContext);
  if (!context) {
    throw new Error('useBlog must be used within a BlogProvider');
  }
  return context;
} 